import {
  Icons,
  LabelChipV2,
  Typography,
  Stack,
  ListItem,
  ListItemText,
  Select,
  tokens,
} from '@cast/design-system';
import { WorkloadOptimizationState } from '@cast/types';

import { useAutoOptimization } from '../../../_hooks/useAutoOptimization';
import type { DisplayPolicy } from './_types';
import { getApplyTimingLabel } from './proposedPoliciesUtils';

type ProposedPolicyCardHeaderProps = {
  displayPolicy: DisplayPolicy;
  expanded: boolean;
  onToggle: () => void;
};

const MANAGEMENT_OPTIONS = [
  WorkloadOptimizationState.MANAGED,
  WorkloadOptimizationState.READ_ONLY,
];

const MANAGEMENT_OPTION_LABELS: Record<string, string> = {
  [WorkloadOptimizationState.MANAGED]: 'Vertical autoscaling',
  [WorkloadOptimizationState.READ_ONLY]: 'Autoscaling disabled',
};

type ManagementSelectProps = {
  name: string;
  managementOption: WorkloadOptimizationState;
};

const ManagementSelect = ({
  name,
  managementOption,
}: ManagementSelectProps) => {
  const { onUpdatePolicyManagementOption } = useAutoOptimization();
  const isManaged = managementOption === WorkloadOptimizationState.MANAGED;

  return (
    <Select
      size="sm"
      value={managementOption}
      onChange={(value) => {
        if (value) onUpdatePolicyManagementOption(name, value);
      }}
      options={MANAGEMENT_OPTIONS}
      sx={{ minWidth: 200 }}
      startAdornment={
        <LabelChipV2
          label="V"
          size="s"
          color="indigo"
          sx={
            isManaged
              ? {
                  bgcolor: tokens.theme.bg.categorical.blueSubtle,
                  color: tokens.theme.fg.categorical.onBlueSubtle,
                }
              : {
                  bgcolor: tokens.theme.bg.status.neutralSubtle,
                  color: tokens.theme.fg.status.onNeutralSubtle,
                }
          }
        />
      }
      renderOption={(option, itemProps) => (
        <ListItem {...itemProps}>
          <ListItemText
            primary={MANAGEMENT_OPTION_LABELS[option]}
            primaryTypographyProps={{ typography: 'P12R' }}
          />
        </ListItem>
      )}
      renderValue={(option) => MANAGEMENT_OPTION_LABELS[option]}
      testId={`management-option-select-${name}`}
    />
  );
};

export const ProposedPolicyCardHeader = ({
  displayPolicy,
  expanded,
  onToggle,
}: ProposedPolicyCardHeaderProps) => {
  const { policy } = displayPolicy;
  const name = policy.payload.name;
  const timing = getApplyTimingLabel(policy);
  const count = policy.targetedWorkloads?.totalCount ?? 0;
  const isImmediate = timing === 'Immediately';
  const managementOption =
    policy.payload.recommendationPolicies?.managementOption ??
    WorkloadOptimizationState.READ_ONLY;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={8}
      onClick={onToggle}
      sx={{ cursor: 'pointer' }}
      data-testid={`proposed-policy-header-${name}`}
    >
      <Stack sx={{ flexShrink: 0 }}>
        {expanded ? <Icons.CaretUp size={16} /> : <Icons.CaretDown size={16} />}
      </Stack>

      <Stack gap={4} flex={1} minWidth={0}>
        <Typography
          variant="A2"
          sx={{ color: tokens.theme.fg.structural.primary }}
        >
          {name}
        </Typography>
        <Stack direction="row" alignItems="center" gap={8}>
          <Typography
            variant="P12R"
            sx={{ color: tokens.theme.fg.structural.secondary }}
          >
            {count} workloads
          </Typography>
          <Typography
            variant="P12R"
            sx={{ color: tokens.theme.fg.structural.secondary }}
          >
            |
          </Typography>
          <LabelChipV2
            label={timing}
            color="green"
            size="m"
            {...(!isImmediate && {
              sx: {
                bgcolor: tokens.theme.bg.status.neutralSubtle,
                color: tokens.theme.fg.status.onNeutralSubtle,
              },
            })}
          />
        </Stack>
      </Stack>

      <Stack onClick={(e) => e.stopPropagation()}>
        <ManagementSelect name={name} managementOption={managementOption} />
      </Stack>
    </Stack>
  );
};
